import { useEffect, useRef, useState, useCallback } from "react";
import type { Editor } from "@tiptap/react";
import { GripVertical, Plus } from "lucide-react";

interface BlockDragHandleProps {
  editor: Editor;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

interface HandlePosition {
  top: number;
  visible: boolean;
}

function getEditorView(editor: Editor) {
  try {
    return editor.view;
  } catch {
    return null;
  }
}

export function BlockDragHandle({ editor, wrapperRef }: BlockDragHandleProps) {
  const [position, setPosition] = useState<HandlePosition>({
    top: 0,
    visible: false,
  });
  const [dragging, setDragging] = useState(false);
  const currentNodeRef = useRef<HTMLElement | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showHandle = useCallback((top: number) => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
    setPosition({ top, visible: true });
  }, []);

  const hideHandle = useCallback(() => {
    hideTimeoutRef.current = setTimeout(() => {
      setPosition((prev) => ({ ...prev, visible: false }));
    }, 150);
  }, []);

  const updateHandlePosition = useCallback(
    (event: MouseEvent) => {
      if (dragging) return;

      const view = getEditorView(editor);
      const wrapper = wrapperRef.current;
      if (!view || !wrapper) return;

      const target = event.target as HTMLElement;

      if (!wrapper.contains(target)) {
        hideHandle();
        return;
      }

      const pos = view.posAtCoords({
        left: event.clientX,
        top: event.clientY,
      });
      if (!pos) return;

      const resolvedPos = view.state.doc.resolve(pos.pos);
      const topLevelNode = resolvedPos.node(1);
      if (!topLevelNode) return;

      const topLevelPos = resolvedPos.before(1);
      const domNode = view.nodeDOM(topLevelPos);
      if (!domNode || !(domNode instanceof HTMLElement)) return;

      currentNodeRef.current = domNode;
      const nodeRect = domNode.getBoundingClientRect();
      const wrapperRect = wrapper.getBoundingClientRect();

      showHandle(nodeRect.top - wrapperRect.top);
    },
    [editor, wrapperRef, dragging, showHandle, hideHandle],
  );

  const handleWrapperMouseLeave = useCallback(() => {
    if (!dragging) {
      hideHandle();
    }
  }, [dragging, hideHandle]);

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    wrapper.addEventListener("mousemove", updateHandlePosition);
    wrapper.addEventListener("mouseleave", handleWrapperMouseLeave);

    return () => {
      wrapper.removeEventListener("mousemove", updateHandlePosition);
      wrapper.removeEventListener("mouseleave", handleWrapperMouseLeave);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [wrapperRef, updateHandlePosition, handleWrapperMouseLeave]);

  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      const node = currentNodeRef.current;
      const view = getEditorView(editor);
      if (!node || !view) return;

      setDragging(true);

      const pos = view.posAtDOM(node, 0);
      const resolvedPos = view.state.doc.resolve(pos);
      const topLevelPos = resolvedPos.before(1);
      const topLevelNode = resolvedPos.node(1);

      const slice = view.state.doc.slice(
        topLevelPos,
        topLevelPos + topLevelNode.nodeSize,
      );
      view.dragging = {
        slice,
        move: true,
      };

      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setDragImage(node, 0, 0);
    },
    [editor],
  );

  const handleDragEnd = useCallback(() => {
    setDragging(false);
  }, []);

  const handlePlusClick = useCallback(() => {
    const view = getEditorView(editor);
    const node = currentNodeRef.current;
    if (!view || !node) return;

    const pos = view.posAtDOM(node, 0);
    const resolvedPos = view.state.doc.resolve(pos);
    const endOfBlock = resolvedPos.end(1);

    editor
      .chain()
      .focus()
      .insertContentAt(endOfBlock, { type: "paragraph" })
      .setTextSelection(endOfBlock + 1)
      .run();

    setTimeout(() => {
      editor.chain().focus().insertContent("/").run();
    }, 0);
  }, [editor]);

  const handleMouseEnterHandles = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  return (
    <div
      className="absolute -left-13 flex items-center gap-0.5 transition-opacity duration-150"
      style={{
        top: position.top,
        opacity: position.visible ? 1 : 0,
        pointerEvents: position.visible ? "auto" : "none",
      }}
      onMouseEnter={handleMouseEnterHandles}
      onMouseLeave={hideHandle}
    >
      <button
        type="button"
        onClick={handlePlusClick}
        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground/50 hover:bg-accent hover:text-muted-foreground"
      >
        <Plus className="h-4 w-4" />
      </button>
      <div
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        className="flex h-6 w-6 items-center justify-center rounded text-muted-foreground/50 hover:bg-accent hover:text-muted-foreground"
        style={{ cursor: dragging ? "grabbing" : "grab" }}
      >
        <GripVertical className="h-4 w-4" />
      </div>
    </div>
  );
}
